export interface ChatReply {
  id: string;
  name: string;
  body: string;
}
import type { cardPrizeQuote, StoreCardValueQuote } from '@blockbattle/shared';
import type { RewardCardSummary, RewardCardRarity, RewardCardSource } from '@blockbattle/shared';
import { deliverResultOnce } from './result-outbox.js';
import type {
  ApiResponse,
  AuthSession,
  EnergyPurchaseOffer,
  HomeData,
  PathHomeSummary,
  LeaderboardEntry,
  LeaguesData,
  DailyLeagueData,
  DailyLeagueMembers,
  OwnProfileView,
  ProfileSettingsView,
  PublicProfileView,
  RunResultResponse,
  RunStartResponse,
  SocialIdentity,
  SeasonPrizeForecast,
} from '@blockbattle/shared';
import type { PathGrowthAnalyticsEventName } from '@blockbattle/shared';
import type { DuelAction, GameMove, PathArtifactType } from '@blockbattle/game-core';
import type { VkPurchaseConfirmation } from './vk-purchase.js';
import type { RankedTargetEntry } from './ranked-target.js';
import type { CommerceProductsView } from '@blockbattle/shared';
import { isDiamondShortageError, showDiamondShortage } from './diamond-shortage.js';

export type ClubHubMessage = {
  id: string;
  body: string;
  kind: 'USER' | 'SYSTEM';
  createdAt: string;
  mine: boolean;
  pinned: boolean;
  deleted: boolean;
  user: { id: string; displayName: string; avatarUrl?: string | null; vipUntil?: string | null };
  replyTo: { id: string; body: string; author: string } | null;
  reactions: readonly { emoji: string; count: number; mine: boolean }[];
};

export type ClubHubData = {
  club: {
    id: string;
    name: string;
    emoji: string;
    color: string;
    description: string;
    inviteCode: string;
    role: string;
    memberCount: number;
  };
  glory: number;
  league: { name: string; nextName: string | null; remaining: number; progress: number };
  expedition: {
    stage: number;
    stageCount: number;
    title: string;
    reward: string;
    target: number;
    progress: number;
    remaining: number;
    completedStages: number;
  };
  personal: { todayGlory: number; dailyCap: number };
  chat: {
    unread: number;
    lastReadAt: string;
    pinned: { id: string; body: string; author: string } | null;
    preview: readonly ClubHubMessage[];
  };
  goals: readonly {
    id: string;
    cadence: 'DAILY' | 'WEEKLY' | 'SEASON';
    title: string;
    progress: number;
    target: number;
    reward: string;
  }[];
  project: {
    id: string;
    title: string;
    description: string;
    reward: string;
    contributed: number;
    target: number;
    endsAt: string;
    balance: number;
    myContribution: number;
  };
  treasury: {
    diamonds: number;
    viewerDiamonds: number;
    canPurchase: boolean;
    claimAvailable: boolean;
    inventory: Readonly<Record<PathArtifactType, number>>;
    diamondPrices: Readonly<Record<PathArtifactType, number>>;
  };
  vote: {
    id: string;
    title: string;
    description: string;
    endsAt: string;
    selectedOptionId: string | null;
    options: readonly { id: string; label: string; votes: number }[];
  } | null;
  people: readonly {
    id: string;
    role: string;
    joinedAt: string;
    online: boolean;
    glory: number;
    diamonds: number;
    user: {
      id: string;
      displayName: string;
      avatarUrl?: string | null;
      social?: SocialIdentity;
    };
  }[];
  permissions: { canManage: boolean; canModerate: boolean };
};

export type DuelTournamentView = {
  readonly id: string;
  readonly code: string;
  readonly status: 'OPEN' | 'ACCEPTED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
  readonly entryFee: number;
  readonly maxParticipants: number;
  readonly prizePool: number;
  readonly participantCount: number;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly expiresAt: string;
  readonly creatorId: string;
  readonly winnerId: string | null;
  readonly resolution: string | null;
  readonly isCreator: boolean;
  readonly isParticipant: boolean;
  readonly canJoin: boolean;
  readonly canPlay: boolean;
  readonly myScore: number | null;
  readonly participants: readonly {
    readonly id: string;
    readonly isCurrentUser: boolean;
    readonly score: number | null;
    readonly scoreHidden: boolean;
    readonly user: {
      readonly id: string;
      readonly displayName: string;
      readonly avatarUrl: string | null;
      readonly social?: SocialIdentity | null;
    };
  }[];
};

export type DuelTournamentsData = {
  readonly balance: number;
  readonly rules: {
    readonly maxParticipants: number;
    readonly maxEntryFee: number;
    readonly paidAllowed: boolean;
    readonly paidUnavailableReason: string | null;
  };
  readonly open: readonly DuelTournamentView[];
  readonly mine: readonly DuelTournamentView[];
  readonly archive: readonly DuelTournamentView[];
};

export type DuelTvBroadcast = {
  readonly code: string;
  readonly kind: 'DIRECT' | 'TOURNAMENT';
  readonly completedAt: string;
  readonly prizePool: number;
  readonly participantCount: number;
  readonly finishedCount: number;
  readonly bestScore: number;
  readonly totalActions: number;
  readonly durationMs: number;
  readonly winners: readonly {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    social?: SocialIdentity | null;
  }[];
  readonly participants: readonly {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    social?: SocialIdentity | null;
  }[];
};

export type DuelTvReplay = {
  readonly code: string;
  readonly kind: 'DIRECT' | 'TOURNAMENT';
  readonly seed: number;
  readonly rulesVersion: string;
  readonly completedAt: string;
  readonly prizePool: number;
  readonly startParameter: string;
  readonly participants: readonly {
    readonly id: string;
    readonly user: {
      id: string;
      displayName: string;
      avatarUrl: string | null;
      social?: SocialIdentity | null;
    };
    readonly score: number;
    readonly isWinner: boolean;
    readonly finished: boolean;
    readonly actions: readonly DuelAction[];
    readonly artifactInventory: Readonly<Record<PathArtifactType, number>>;
    readonly stats: {
      readonly actionCount: number;
      readonly moveCount: number;
      readonly artifactCount: number;
      readonly placedPieces: number;
      readonly clearedLines: number;
      readonly maxCombo: number;
      readonly durationMs: number;
    };
  }[];
};

export type ClubBattleView = {
  readonly id: string;
  readonly code: string;
  readonly status: 'CHALLENGED' | 'ACTIVE' | 'COMPLETED' | 'DECLINED' | 'CANCELLED';
  readonly side: 'HOME' | 'AWAY' | null;
  readonly homeClub: { id: string; name: string; emoji: string; color: string };
  readonly awayClub: { id: string; name: string; emoji: string; color: string };
  readonly squadSize: number;
  readonly durationDays: number;
  readonly day: number;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly challengeExpiresAt: string | null;
  readonly homePoints: number;
  readonly awayPoints: number;
  readonly homeTotal: number;
  readonly awayTotal: number;
  readonly homeParticipation: number;
  readonly awayParticipation: number;
  readonly flagPosition: number;
  readonly escrow: {
    readonly home: { diamonds: number; coins: number };
    readonly away: { diamonds: number; coins: number };
  };
  readonly stakePreview: {
    readonly home: { diamonds: number; coins: number };
    readonly away: { diamonds: number; coins: number };
  };
  readonly arsenal: {
    readonly home: readonly { artifact: PathArtifactType; quantity: number }[];
    readonly away: readonly { artifact: PathArtifactType; quantity: number }[];
  };
  readonly myBestScore: number | null;
  readonly canAccept: boolean;
  readonly canCancel: boolean;
  readonly canDecline: boolean;
  readonly canPlay: boolean;
  readonly spectatorReason: string | null;
  readonly days: readonly {
    day: number;
    played: boolean;
    homePoints: number;
    awayPoints: number;
    homeTotal: number;
    awayTotal: number;
    winner: 'HOME' | 'AWAY' | 'TIE';
  }[];
  readonly results: readonly {
    clubId: string;
    day: number;
    bestScore: number;
    artifactUses: number;
    user: { id: string; displayName: string; avatarUrl: string | null };
  }[];
};

export type ClubBattlesData = {
  readonly clubId: string | null;
  readonly canManage: boolean;
  readonly battles: readonly ClubBattleView[];
  readonly opponents: readonly {
    id: string;
    name: string;
    emoji: string;
    color: string;
    _count: { members: number };
  }[];
};

export type StoreItem = {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly description: string;
  readonly type: string;
  readonly category: 'ARENAS' | 'EFFECTS' | 'PROFILE' | 'CLUB';
  readonly price: number;
  readonly rarity: string;
  readonly visibleIn: readonly string[];
  readonly ownership: 'PERMANENT' | 'UNLOCK';
  readonly rental: { readonly durationDays: 3; readonly price: number } | null;
  readonly giftable: boolean;
  readonly collectionKey: string | null;
  readonly source: string;
  readonly published: boolean;
  readonly owned: boolean;
  readonly equipped: boolean;
  readonly scope: 'PLAYER' | 'CLUB';
  readonly clubId: string | null;
  readonly available: boolean;
  readonly expiresAt: string | null;
  readonly preview?: {
    readonly color?: string;
    readonly colors?: readonly string[];
    readonly icon?: string;
  };
};

export type GemPack = {
  readonly key: string;
  readonly baseGems: number;
  readonly bonusGems: number;
  readonly totalGems: number;
  readonly externalProductId: string;
  readonly platform: 'VK' | 'MAX' | 'WEB';
  readonly price: { readonly amount: number; readonly currency: 'VK_VOTES' | 'RUB' };
  readonly badge?: string;
};

export type StoreData = {
  readonly balance: number;
  readonly currency: { readonly code: 'DIAMONDS'; readonly label: 'Алмазы' };
  readonly platform: 'VK' | 'MAX' | 'WEB';
  readonly gemPacks: readonly GemPack[];
  readonly purchaseAvailability: { readonly enabled: boolean; readonly message: string | null };
  readonly receiptEmailRequired: boolean;
  readonly earnMethods: readonly {
    readonly id: string;
    readonly title: string;
    readonly description: string;
    readonly rewardLabel: string;
    readonly href: string;
  }[];
  readonly season: {
    readonly key: string;
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly assetUrl: string;
  };
  readonly categories: readonly { readonly id: string; readonly label: string }[];
  readonly items: readonly StoreItem[];
  readonly gifts: readonly {
    readonly id: string;
    readonly senderId: string;
    readonly recipientId: string;
    readonly pricePaid: number;
    readonly message: string | null;
    readonly createdAt: string;
    readonly sender: {
      readonly id: string;
      readonly displayName: string;
      readonly avatarUrl: string | null;
    };
    readonly recipient: {
      readonly id: string;
      readonly displayName: string;
      readonly avatarUrl: string | null;
    };
    readonly cosmetic: { readonly id: string; readonly name: string; readonly type: string };
  }[];
  readonly history: {
    readonly wallet: readonly {
      readonly id: string;
      readonly amount: number;
      readonly balance: number;
      readonly reason: string;
      readonly reference: string | null;
      readonly source: string | null;
      readonly status: string;
      readonly createdAt: string;
    }[];
    readonly orders: readonly {
      readonly id: string;
      readonly productKey: string | null;
      readonly gemAmount: number;
      readonly provider: string;
      readonly status: string;
      readonly createdAt: string;
      readonly paidAt: string | null;
    }[];
  };
};
export type PaymentReviewData = {
  readonly label: string;
  readonly expiresAt: string;
  readonly balance: number;
  readonly testMode: boolean;
  readonly receiptEmailRequired: boolean;
  readonly ready: boolean;
  readonly packs: readonly GemPack[];
};

export type PathArtifact = 'LASER' | 'ROCKET' | 'PLASMA' | 'IMPULSE' | 'REROLL' | 'UNDO';
export type PathNode = {
  readonly earnedCoins?: number;
  readonly maxCoins?: number;
  readonly levelNumber: number;
  readonly seed: number;
  readonly targetScore: number;
  readonly twoStarScore: number;
  readonly threeStarScore: number;
  readonly nodeType: 'LEVEL' | 'CARD' | 'CHALLENGE' | 'MILESTONE' | 'BOSS';
  readonly zone: 'NEON' | 'GOLD' | 'ICE' | 'DARK' | 'CRYSTAL' | 'FIRE';
  readonly poolTier: 'A' | 'B' | 'C' | 'D' | 'E';
  readonly x: number;
  readonly state: 'COMPLETED' | 'CURRENT' | 'LOCKED';
  readonly stars: number;
  readonly bestScore: number;
};
export type PathCard = {
  readonly rewardVersion?: number;
  readonly rewardOrigin?: string | null;
  readonly createdAt?: string;
  readonly bonusCoins?: number;
  readonly id: string;
  readonly levelNumber: number | null;
  readonly source: 'PATH' | 'STORE' | 'PASS' | 'TASK';
  readonly rarity: 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';
  readonly rewardType: 'DIAMONDS' | 'ENERGY' | 'SOFT_CURRENCY' | 'ARTIFACT';
  readonly rewardAmount: number;
  readonly artifact: PathArtifact | null;
  readonly purchasePrice: number | null;
  readonly status: 'READY' | 'REVEALED' | 'CLAIMED';
  readonly scratchPercent: number;
};
export type StoreCardOffer = {
  readonly prizeQuote?: ReturnType<typeof cardPrizeQuote>;
  readonly value?: StoreCardValueQuote;
  readonly guaranteedArtifact?: PathArtifact;
  readonly rarity: PathCard['rarity'];
  readonly title: string;
  readonly price: number;
  readonly odds: {
    readonly softCurrency: number;
    readonly energy: number;
    readonly artifact: number;
    readonly diamonds: number;
  };
};
export type StoreCardData = {
  readonly guaranteedOffers?: readonly {
    artifact: PathArtifact;
    price: number;
    bonusCoins: number;
  }[];
  readonly enabled: boolean;
  readonly balance: number;
  readonly dailyLimit: number;
  readonly purchasedToday: number;
  readonly remainingToday: number;
  readonly offers: readonly StoreCardOffer[];
  readonly cards: readonly PathCard[];
};
export type PathData = {
  readonly progress: {
    readonly currentLevel: number;
    readonly highestLevelCompleted: number;
    readonly totalStars: number;
    readonly softCurrency: number;
    readonly cleanHighestLevelCompleted: number;
    readonly cleanTotalStars: number;
    readonly cleanMasteryAt: string | null;
  };
  readonly wallet: {
    readonly diamonds: number;
    readonly energy: number;
    readonly playableEnergy: number;
    readonly freeEnergy: number;
    readonly pathBonusEnergy: number;
    readonly pathEarnedEnergy: number;
    readonly pathPurchasedEnergy: number;
    readonly softCurrency: number;
  };
  readonly energyCost: number;
  readonly energyOffer: PathEnergyOffer;
  readonly nodes: readonly PathNode[];
  readonly inventory: Readonly<Record<PathArtifact, number>>;
  readonly prices: Readonly<Record<PathArtifact, number>>;
  readonly workshop: {
    readonly enabled: boolean;
    readonly dailyLimit: number;
    readonly craftedToday: number;
    readonly remainingCrafts: number;
    readonly costs: Readonly<Record<PathArtifact, number>>;
  };
  readonly currentCard: PathCard | null;
  readonly history: readonly {
    readonly id: string;
    readonly levelNumber: number;
    readonly nodeType: string;
    readonly bestScore: number;
    readonly stars: number;
    readonly completedAt: string | null;
  }[];
  readonly achievements: readonly {
    readonly key: string;
    readonly title: string;
    readonly current: number;
    readonly target: number;
  }[];
  readonly leaders: readonly {
    readonly rank: number;
    readonly userId: string;
    readonly name: string;
    readonly avatarUrl: string | null;
    readonly level: number;
    readonly stars: number;
    readonly isYou: boolean;
  }[];
  readonly leaderboard: {
    readonly kind: 'CLEAN_MASTERY';
    readonly assistedRunsExcluded: true;
    readonly myRank: number | null;
  };
};

export type PathEnergyOffer = EnergyPurchaseOffer;

const configuredUrl: unknown = import.meta.env['VITE_PLAYER_API_URL'];
const API_URL = typeof configuredUrl === 'string' ? configuredUrl : 'http://localhost:3000/api/v1';
const API_TIMEOUT_MS = 12_000;

export class ApiError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

let renewingSession: Promise<void> | null = null;
export const renewPlayerSession = async (): Promise<void> => {
  if (!renewingSession) {
    renewingSession = (async () => {
      const refreshToken = sessionStorage.getItem('bb_refresh_token');
      if (!refreshToken)
        throw new ApiError(
          'SESSION_EXPIRED',
          'Войдите в игру заново. Журнал партии сохранён на устройстве.',
          401,
        );
      const result = await request<{ accessToken: string; userId: string }>(
        '/auth/refresh',
        {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        },
        false,
      );
      if (result.userId !== sessionStorage.getItem('bb_session_owner'))
        throw new ApiError(
          'SESSION_CHANGED',
          'Аккаунт изменился. Откройте исходный аккаунт для сохранения партии.',
          403,
        );
      sessionStorage.setItem('bb_access_token', result.accessToken);
    })().finally(() => {
      renewingSession = null;
    });
  }
  return renewingSession;
};

const request = async <T>(
  path: string,
  options: RequestInit = {},
  authenticated = true,
  timeoutMs = API_TIMEOUT_MS,
  retryAuth = true,
): Promise<T> => {
  const token = sessionStorage.getItem('bb_access_token');
  let response: Response;
  const controller = new AbortController();
  let timedOut = false;
  const timer = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const abort = () => controller.abort();
  if (options.signal?.aborted) abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  try {
    try {
      response = await fetch(`${API_URL}${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(authenticated && token ? { authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });
    } catch {
      if (timedOut)
        throw new ApiError('API_TIMEOUT', 'Сервер игры не ответил вовремя. Попробуйте ещё раз.', 0);
      throw new ApiError('NETWORK_ERROR', 'Сервер игры недоступен. Проверьте подключение.', 0);
    }
    if (response.status === 401 && authenticated && retryAuth) {
      globalThis.clearTimeout(timer);
      if (sessionStorage.getItem('bb_access_token') === token) await renewPlayerSession();
      return request<T>(path, options, authenticated, timeoutMs, false);
    }
    let body: ApiResponse<T>;
    try {
      body = (await response.json()) as ApiResponse<T>;
    } catch {
      throw new ApiError(
        'INVALID_API_RESPONSE',
        `Сервер игры вернул некорректный ответ (HTTP ${response.status}).`,
        response.status,
      );
    }
    if (!body.ok) {
      if (isDiamondShortageError(body.error)) showDiamondShortage({ source: 'api' });
      throw new ApiError(body.error.code, body.error.message, response.status, body.requestId);
    }
    return body.data;
  } finally {
    globalThis.clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
  }
};

export const api = {
  resultShareCard: async (runId: string) => {
    const result = await request<{ path: string }>('/results/share-card', {
      method: 'POST',
      body: JSON.stringify({ runId }),
    });
    return new URL(
      `${API_URL.replace(/\/$/, '')}${result.path}`,
      window.location.origin,
    ).toString();
  },
  authenticate: (body: unknown) =>
    request<AuthSession>('/auth/platform', { method: 'POST', body: JSON.stringify(body) }, false),
  publicConfig: () =>
    request<{
      integrations: {
        vk?: { enabled: boolean; launchUrl: string | null; appId?: string };
        max?: { enabled: boolean; launchUrl: string | null; botUsername?: string };
      };
      documents: readonly {
        type: string;
        version: string;
        title: string;
        markdown: string;
        publishedAt: string | null;
      }[];
      legal: {
        operatorName: string | null;
        legalName: string | null;
        email: string | null;
        address: string | null;
        details: string | null;
        versionDate: string | null;
      };
      commerce: {
        product: string;
        delivery: string;
        usage: string;
        refund: string;
        packs: readonly {
          key: string;
          gems: number;
          baseGems: number;
          bonusGems: number;
          amount: number;
          currency: 'RUB';
        }[];
      };
    }>('/config/public', {}, false),
  paymentReview: (token: string) =>
    request<PaymentReviewData>(`/payment-review/${encodeURIComponent(token)}`, {}, false),
  paymentReviewCheckout: (token: string, key: string, idempotencyKey: string, email: string) =>
    request<{ orderId: string; confirmationUrl: string }>(
      `/payment-review/${encodeURIComponent(token)}/checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ key, idempotencyKey, email }),
      },
      false,
    ),
  recoverPaymentReviewOrder: (token: string, orderId: string) =>
    request<{ status: string; balance: number; gemAmount: number }>(
      `/payment-review/${encodeURIComponent(token)}/orders/${encodeURIComponent(orderId)}/recover`,
      { method: 'POST' },
      false,
    ),
  home: () => request<HomeData>('/home'),
  commerceJourney: () =>
    request<{
      cohort: 'launch' | 'control';
      showAnnouncement: boolean;
      intent: { kind: 'COSMETIC' | 'CARD' | 'ARTIFACT' | 'PASS'; key: string } | null;
      offer?: { kind: 'PASS'; key: string; steps: number } | null;
      checkoutPackKey: string | null;
      campaignId: string | null;
    }>('/commerce/journey'),
  commerceAction: (
    body: {
      action:
        | 'announcement_shown'
        | 'announcement_dismissed'
        | 'announcement_clicked'
        | 'intent'
        | 'checkout'
        | 'offer_opened'
        | 'unsubscribe'
        | 'subscribe'
        | 'intent_cleared';
      kind?: 'COSMETIC' | 'CARD' | 'ARTIFACT' | 'PASS';
      source?: 'store' | 'artifact' | 'pass' | 'no_moves' | 'home';
      key?: string;
      campaign?:
        | 'native_launch_v1'
        | 'bot_launch_v1'
        | 'checkout_return_v1'
        | 'product_interest_v1'
        | 'pass_progress_v1'
        | 'diamond_nurture_1_v1'
        | 'diamond_nurture_2_v1'
        | 'diamond_nurture_3_v1'
        | 'diamond_nurture_4_v1'
        | 'diamond_nurture_5_v1';
    },
    timeoutMs?: number,
  ) =>
    request<{ accepted: boolean }>(
      '/commerce/journey',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      true,
      timeoutMs,
    ),
  path: () => request<PathData>('/path'),
  pathSummary: () => request<PathHomeSummary>('/path/summary'),
  dailyLeagues: (offset = 0) =>
    request<DailyLeagueData>(`/daily-leagues${offset ? `?offset=${offset}` : ''}`),
  dailyLeagueMembers: (after?: string) =>
    request<DailyLeagueMembers>(
      `/daily-leagues/members${after ? `?after=${encodeURIComponent(after)}` : ''}`,
    ),
  artifactCatalog: () =>
    request<
      Pick<PathData, 'inventory' | 'prices' | 'workshop'> & {
        wallet: Pick<PathData['wallet'], 'diamonds' | 'softCurrency'>;
      }
    >('/path/artifacts'),
  startPathLevel: (level: number, requestId?: string, resumeRunId?: string) =>
    request<{
      runId: string;
      level: PathNode;
      inventory: Readonly<Record<PathArtifact, number>>;
      expiresAt: string;
      resumed: boolean;
    }>(`/path/levels/${level}/start`, {
      method: 'POST',
      body: JSON.stringify({ requestId, resumeRunId }),
    }),
  finishPathRun: (id: string, actions: readonly unknown[]) =>
    request<{
      completed: boolean;
      score: number;
      stars: number;
      rewards?: { softCurrency: number; diamonds: number };
      energyReturned?: number;
      earnedCoins?: number;
      maxCoins?: number;
      assisted?: boolean;
      leaderboardEligible?: boolean;
      nextLevel?: number;
    }>(
      `/path/runs/${id}/finish`,
      { method: 'POST', body: JSON.stringify({ actions }) },
      true,
      30_000,
    ),
  failPathRun: (id: string, actions: readonly unknown[], quit = false) =>
    request<{ failed: true; score: number; duplicate?: boolean }>(`/path/runs/${id}/fail`, {
      method: 'POST',
      body: JSON.stringify({ actions, ...(quit ? { quit: true } : {}) }),
    }),
  friends: () => request<FriendConnection[]>('/friends'),
  findFriends: (q: string) => request<ChatPerson[]>(`/friends/search?q=${encodeURIComponent(q)}`),
  addFriend: (targetId: string) =>
    request('/friends', { method: 'POST', body: JSON.stringify({ targetId }) }),
  updateFriend: (id: string, action: 'ACCEPT' | 'REMOVE') =>
    request(`/friends/${id}`, { method: 'POST', body: JSON.stringify({ action }) }),
  privateMessages: (id: string, before?: string) =>
    request<{
      canWrite: boolean;
      writeReason?: 'VIP_REQUIRED' | 'RESTRICTED' | 'FRIEND_REQUIRED' | null;
      messages: {
        id: string;
        senderId: string;
        body: string;
        createdAt: string;
        replyTo?: ChatReply | null;
      }[];
    }>(`/friends/${id}/messages${before ? `?before=${before}` : ''}`),
  sendPrivateMessage: (id: string, body: string, requestKey: string, replyToId?: string) =>
    request(`/friends/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, requestKey, replyToId }),
    }),
  readPrivateMessages: (id: string, messageId?: string) =>
    request(`/friends/${id}/read`, { method: 'POST', body: JSON.stringify({ messageId }) }),
  chatAttention: () =>
    request<{ global: number; private: number; requests: number }>('/chat/attention'),
  readChat: (messageId: string) =>
    request('/chat/read', { method: 'POST', body: JSON.stringify({ messageId }) }),
  claimCommonCards: (ids: string[]) =>
    request<{
      rewards: {
        rewardType: string;
        rewardAmount: number;
        bonusCoins?: number;
        artifact?: string | null;
        duplicate?: boolean;
      }[];
    }>('/cards/claim-common', { method: 'POST', body: JSON.stringify({ ids }) }),
  rewardCardCount: () =>
    request<RewardCardSummary & { guarantees?: { rare: number; epic: number; legendary: number } }>(
      '/cards/count',
    ),
  rewardCards: (after?: string, rarity?: RewardCardRarity, source?: RewardCardSource) => {
    const params = new URLSearchParams({
      ...(after ? { after } : {}),
      ...(rarity ? { rarity } : {}),
      ...(source ? { source } : {}),
    });
    return request<PathCard[]>(`/cards${params.size ? `?${params}` : ''}`);
  },
  chat: (before?: string) =>
    request<{
      canWrite: boolean;
      muted: boolean;
      blockedUsers: string[];
      messages: {
        id: string;
        body: string;
        createdAt: string;
        replyTo?: ChatReply | null;
        user: {
          id: string;
          displayName: string;
          avatarUrl: string | null;
          social?: SocialIdentity;
        };
      }[];
    }>(`/chat${before ? `?before=${encodeURIComponent(before)}` : ''}`),
  sendChat: (body: string, requestKey: string, replyToId?: string) =>
    request<{ id: string }>('/chat', {
      method: 'POST',
      body: JSON.stringify({ body, requestKey, replyToId }),
    }),
  reportChat: (id: string, reason: string) =>
    request(`/chat/${id}/report`, { method: 'POST', body: JSON.stringify({ reason }) }),
  blockChat: (targetId: string, blocked: boolean) =>
    request('/chat/blocks', { method: 'POST', body: JSON.stringify({ targetId, blocked }) }),
  scratchPathCard: (id: string, percent: number) =>
    request<PathCard>(`/path/cards/${id}/scratch`, {
      method: 'PATCH',
      body: JSON.stringify({ percent }),
    }),
  collectPathCard: (id: string) =>
    request(`/path/cards/${id}/collect`, { method: 'POST', body: '{}' }),
  claimPathCard: (id: string) =>
    request<{
      claimed: boolean;
      rewardType: string;
      rewardAmount: number;
      artifact?: PathArtifact | null;
      nextLevel?: number;
    }>(`/path/cards/${id}/claim`, { method: 'POST' }),
  storeCards: () => request<StoreCardData>('/path/cards/store'),
  buyStoreCard: (
    rarity: PathCard['rarity'],
    idempotencyKey: string,
    guaranteedArtifact?: PathArtifact,
    expectedPrice?: number,
  ) =>
    request<{ purchased: true; duplicate: boolean; balance: number; card: PathCard }>(
      '/path/cards/store/purchase',
      {
        method: 'POST',
        body: JSON.stringify({ rarity, idempotencyKey, guaranteedArtifact, expectedPrice }),
      },
    ),
  buyPathArtifact: (artifact: PathArtifact, idempotencyKey: string) =>
    request<{ purchased: boolean; artifact: PathArtifact; quantity: number; balance: number }>(
      `/path/artifacts/${artifact}/buy`,
      { method: 'POST', body: JSON.stringify({ idempotencyKey }) },
    ),
  craftPathArtifact: (artifact: PathArtifact, idempotencyKey: string) =>
    request<{
      crafted: boolean;
      duplicate: boolean;
      artifact: PathArtifact;
      quantity: number;
      softCurrency: number;
      softCurrencyBefore: number;
      cost: number;
      workshop: PathData['workshop'];
    }>(`/path/artifacts/${artifact}/craft`, {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey }),
    }),
  energyOffer: () => request<PathEnergyOffer>('/energy/offer'),
  buyEnergy: (idempotencyKey: string) =>
    request<{
      purchased: true;
      duplicate: boolean;
      energy: number;
      freeEnergy: number;
      pathBonusEnergy: number;
      playableEnergy: number;
      pathBonusBefore: number;
      pathPurchasedBefore: number;
      diamonds: number;
      diamondsBefore: number;
      energyGranted: number;
      diamondsSpent: number;
      offer: PathEnergyOffer;
    }>('/energy/buy', {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey }),
    }),
  startRewardedEnergyAd: () =>
    request<{
      claimId: string;
      reward: number;
      watchedLast24Hours: number;
      limitPer24Hours: number;
      remaining: number;
      resetAt: string | null;
    }>('/energy/rewarded-ad/start', { method: 'POST' }),
  completeRewardedEnergyAd: (claimId: string) =>
    request<{
      current: number;
      max: number;
      reward: number;
      watchedLast24Hours: number;
      limitPer24Hours: number;
      remaining: number;
      resetAt: string | null;
    }>('/energy/rewarded-ad/complete', {
      method: 'POST',
      body: JSON.stringify({ claimId }),
    }),
  claimLeagueReward: (id: string) =>
    request<{ reward: number; balance: number }>(`/home/league-rewards/${id}/claim`, {
      method: 'POST',
    }),
  rewards: () =>
    request<{
      daily: readonly ActivityTask[];
      weekly: readonly ActivityTask[];
      introduction?: readonly ActivityTask[];
      expeditionSeason?: readonly ActivityTask[];
      periodKey?: string;
      awarded?: number;
      weeklyRanking: {
        currentRank: number | null;
        currentScore: number;
        projectedReward: number;
        previous: null | {
          id: string;
          periodKey: string;
          rank: number;
          score: number;
          reward: number;
          claimed: boolean;
        };
      };
      season: {
        current: { key: string; name: string; icon: string; endsAt: string };
        currentRank: number | null;
        currentScore: number;
        previous: null | {
          id: string;
          periodKey: string;
          rank: number;
          score: number;
          reward: number;
          claimed: boolean;
        };
      };
    }>('/rewards'),
  replaceQuest: (id: string) =>
    request<{ replaced: boolean }>(`/rewards/tasks/${id}/replace`, { method: 'POST' }),
  claimTaskReward: (id: string) =>
    request<{
      card?: PathCard;
      reward: number;
      balance: number;
      resource: 'PATH_COINS' | 'ENERGY';
    }>(`/rewards/tasks/${id}/claim`, { method: 'POST' }),
  claimRankingReward: (id: string) =>
    request<{ reward: number; balance: number }>(`/rewards/rankings/${id}/claim`, {
      method: 'POST',
    }),
  startRun: (
    mode: 'DAILY' | 'PRACTICE' | 'DUEL',
    duelCode?: string,
    requestId?: string,
    clubBattleId?: string,
  ) =>
    request<RunStartResponse>('/game/runs/start', {
      method: 'POST',
      body: JSON.stringify({
        mode,
        ...(mode === 'DAILY' ? { rankedArtifacts: true } : {}),
        ...(duelCode ? { duelCode } : {}),
        ...(clubBattleId ? { clubBattleId } : {}),
        ...(requestId ? { requestId } : {}),
      }),
    }),
  finishRun: (
    id: string,
    token: string,
    moves: readonly unknown[],
    actions?: readonly unknown[],
    forfeit = false,
  ) =>
    deliverResultOnce(id, () =>
      request<RunResultResponse>(
        `/game/runs/${id}/finish`,
        {
          method: 'POST',
          body: JSON.stringify({
            token,
            moves,
            ...(actions ? { actions } : {}),
            ...(forfeit ? { forfeit: true } : {}),
            clientVersion: '0.1.0',
          }),
        },
        true,
        45_000,
      ),
    ),
  useRankedArtifact: (
    id: string,
    token: string,
    actions: readonly unknown[],
    idempotencyKey: string,
  ) =>
    request<{
      actions: NonNullable<RunStartResponse['resumeActions']>;
      artifactInventory: NonNullable<RunStartResponse['artifactInventory']>;
      rescues?: RunStartResponse['rescues'];
    }>(`/game/runs/${id}/artifact`, {
      method: 'POST',
      body: JSON.stringify({ token, actions, idempotencyKey }),
    }),
  buyRankedRescue: (
    id: string,
    token: string,
    moves: readonly unknown[],
    idempotencyKey: string,
    actions?: readonly unknown[],
  ) =>
    request<{
      purchased: true;
      duplicate: boolean;
      price: number;
      balance: number;
      rescue: {
        afterMoveCount: number;
        pieceIds: readonly [string, string, string];
      };
    }>(`/game/runs/${id}/rescue`, {
      method: 'POST',
      body: JSON.stringify({ token, moves, idempotencyKey, ...(actions ? { actions } : {}) }),
    }),
  abandonRun: (id: string) =>
    request<{ abandoned: boolean; refundedAttempt: boolean }>(`/game/runs/${id}/abandon`, {
      method: 'POST',
    }),
  dailyTargets: () =>
    request<{ entries: readonly RankedTargetEntry[] }>('/leaderboards/daily-targets'),
  commerceProducts: () => request<CommerceProductsView>('/commerce/products'),
  acknowledgeVipTrial: (kind: 'INTRO' | 'EXPIRY') =>
    request<{ acknowledged: true }>('/commerce/vip-trial/seen', {
      method: 'POST',
      body: JSON.stringify({ kind }),
    }),
  acknowledgePassRewards: (body: {
    periodKey: string;
    freeSeen: number;
    premiumSeen: number;
    welcomeSeen: boolean;
  }) =>
    request<{ acknowledged: true }>('/commerce/passes/seen', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  buyCommerceProduct: (
    body:
      | { kind: 'VIP_TRIAL'; expectedPrice: number; idempotencyKey: string }
      | { kind: 'PASS'; periodKey: string; expectedPrice: number; idempotencyKey: string }
      | { kind: 'BUNDLE'; key: string; expectedPrice: number; idempotencyKey: string }
      | { kind: 'CLUB_GIFT'; clubId: string; expectedPrice: number; idempotencyKey: string },
  ) =>
    request<{ purchased: true }>('/commerce/products/purchase', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  leaderboard: (period: 'day' | 'week' | 'month' | 'season' | 'all' | 'clubs') =>
    request<{
      top: readonly LeaderboardEntry[];
      aroundMe: readonly LeaderboardEntry[];
      myRank: number | null;
      period?: 'day' | 'week' | 'month' | 'season';
      metric?: 'SCORE' | 'RP';
      startsAt?: string;
      season?: { startsAt: string; endsAt: string; name: string };
      prizeForecast?: SeasonPrizeForecast;
    }>(period === 'clubs' ? '/leaderboards/clubs' : `/leaderboards?period=${period}`),
  leagues: () => request<LeaguesData>('/leagues'),
  profile: () => request<OwnProfileView>('/profile'),
  publicProfile: (id: string) => request<PublicProfileView>(`/profiles/${id}`),
  updateProfileSettings: (values: ProfileSettingsView) =>
    request<ProfileSettingsView>('/profile/settings', {
      method: 'PATCH',
      body: JSON.stringify(values),
    }),
  updateProfileShowcase: (achievementKeys: readonly string[]) =>
    request<{ achievementKeys: readonly string[] }>('/profile/showcase', {
      method: 'PUT',
      body: JSON.stringify({ achievementKeys }),
    }),
  clubs: (search = '') =>
    request<readonly Record<string, unknown>[]>(
      `/clubs${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ),
  club: (id: string) => request<Record<string, unknown>>(`/clubs/${id}`),
  clubHub: (id: string) => request<ClubHubData>(`/clubs/${id}/hub`),
  createClub: (values: Record<string, unknown>) =>
    request<Record<string, unknown>>('/clubs', {
      method: 'POST',
      body: JSON.stringify(values),
    }),
  updateClub: (id: string, values: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/clubs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(values),
    }),
  contributeToClub: (id: string, amount: number) =>
    request<{ amount: number; score: number; balance: number }>(`/clubs/${id}/contributions`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  contributeToClubTreasury: (
    id: string,
    resource: 'DIAMONDS',
    amount: number,
    idempotencyKey: string,
  ) =>
    request<{ contributed: boolean; balance: number; duplicate: boolean }>(
      `/clubs/${id}/treasury/contributions`,
      {
        method: 'POST',
        body: JSON.stringify({ resource, amount, idempotencyKey }),
      },
    ),
  buyClubArtifact: (
    id: string,
    artifact: PathArtifactType,
    resource: 'DIAMONDS',
    idempotencyKey: string,
  ) =>
    request<{ purchased: boolean; balance: number; quantity: number; duplicate: boolean }>(
      `/clubs/${id}/treasury/artifacts/${artifact}/buy`,
      {
        method: 'POST',
        body: JSON.stringify({ resource, idempotencyKey }),
      },
    ),
  claimClubArtifact: (id: string, artifact: PathArtifactType) =>
    request<{ claimed: boolean; artifact: PathArtifactType; personalQuantity: number }>(
      `/clubs/${id}/treasury/artifacts/${artifact}/claim`,
      { method: 'POST' },
    ),
  clubMessages: (id: string, cursor?: string) =>
    request<{ items: readonly ClubHubMessage[]; nextCursor: string | null }>(
      `/clubs/${id}/messages${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  sendClubMessage: (id: string, body: string, clientId: string, replyToId?: string) =>
    request<ClubHubMessage>(`/clubs/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, clientId, ...(replyToId ? { replyToId } : {}) }),
    }),
  markClubMessagesRead: (id: string, messageId: string) =>
    request<{ lastReadAt: string }>(`/clubs/${id}/messages/read`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),
  reactToClubMessage: (id: string, messageId: string, emoji: string) =>
    request<{ active: boolean }>(`/clubs/${id}/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),
  moderateClubMessage: (
    id: string,
    messageId: string,
    values: { pinned?: boolean; deleted?: boolean },
  ) =>
    request<{ updated: boolean }>(`/clubs/${id}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify(values),
    }),
  reportClubMessage: (id: string, messageId: string, reason: string) =>
    request<{ accepted: boolean }>(`/clubs/${id}/messages/${messageId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  blockClubUser: (id: string, userId: string, blocked: boolean) =>
    request<{ blocked: boolean }>(`/clubs/${id}/users/${userId}/block`, {
      method: 'POST',
      body: JSON.stringify({ blocked }),
    }),
  contributeToClubProject: (
    id: string,
    projectId: string,
    amount: number,
    idempotencyKey: string,
  ) =>
    request<{
      amount: number;
      contributed: number;
      target: number;
      balance: number;
      completed: boolean;
      replayed: boolean;
    }>(`/clubs/${id}/projects/${projectId}/contributions`, {
      method: 'POST',
      body: JSON.stringify({ amount, idempotencyKey }),
    }),
  voteInClub: (id: string, voteId: string, optionId: string) =>
    request<{ selectedOptionId: string }>(`/clubs/${id}/votes/${voteId}`, {
      method: 'POST',
      body: JSON.stringify({ optionId }),
    }),
  updateClubMember: (
    id: string,
    userId: string,
    values: { role?: 'ADMIN' | 'MODERATOR' | 'MEMBER'; chatRestrictedMinutes?: number },
  ) =>
    request<{ role: string; chatRestrictedUntil: string | null }>(
      `/clubs/${id}/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify(values) },
    ),
  recordCommunityVisit: (id: string) =>
    request<{ recorded: boolean; url: string }>(`/clubs/${id}/community/visit`, { method: 'POST' }),
  joinClub: (id: string, code?: string) =>
    request<{ joined: boolean }>(`/clubs/${id}/join`, {
      method: 'POST',
      body: JSON.stringify(code ? { code } : {}),
    }),
  leaveClub: (id: string) => request<{ left: boolean }>(`/clubs/${id}/leave`, { method: 'POST' }),
  clubInvite: (code: string) =>
    request<{
      id: string;
      name: string;
      emoji: string;
      description: string;
      membership:
        { state: 'NONE'; clubId: null } | { state: 'THIS_CLUB' | 'OTHER_CLUB'; clubId: string };
    }>(`/clubs/invites/${code}`),
  joinClubInvite: (code: string) =>
    request<{ joined: boolean; alreadyMember: boolean; clubId: string }>(
      `/clubs/invites/${code}/join`,
      { method: 'POST' },
    ),
  createDuel: (runId: string) =>
    request<{ code: string; startParameter: string }>('/duels', {
      method: 'POST',
      body: JSON.stringify({ runId }),
    }),
  challengePlayer: (targetUserId: string) =>
    request<{ code: string; startParameter: string; reused: boolean }>('/duels/challenge', {
      method: 'POST',
      body: JSON.stringify({ targetUserId }),
    }),
  createOpenDuel: () =>
    request<{ code: string; startParameter: string }>('/duels/open', { method: 'POST' }),
  duelTournaments: () => request<DuelTournamentsData>('/duel-tournaments'),
  duelTv: () => request<{ broadcasts: readonly DuelTvBroadcast[] }>('/duel-tv'),
  duelTvReplay: (code: string) => request<DuelTvReplay>(`/duel-tv/${code}`),
  clubBattles: () => request<ClubBattlesData>('/club-battles'),
  clubBattle: (id: string) => request<ClubBattleView>(`/club-battles/${id}`),
  createClubBattle: (opponentClubId: string, requestId: string) =>
    request<ClubBattleView>('/club-battles', {
      method: 'POST',
      body: JSON.stringify({ opponentClubId, requestId }),
    }),
  acceptClubBattle: (id: string, requestId: string) =>
    request<ClubBattleView>(`/club-battles/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),
  cancelClubBattle: (id: string, requestId: string) =>
    request<ClubBattleView>(`/club-battles/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),
  declineClubBattle: (id: string, requestId: string) =>
    request<ClubBattleView>(`/club-battles/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),
  createDuelTournament: (entryFee: number, maxParticipants: number, requestId: string) =>
    request<DuelTournamentView>('/duel-tournaments', {
      method: 'POST',
      body: JSON.stringify({ entryFee, maxParticipants, requestId }),
    }),
  joinDuelTournament: (code: string, requestId: string) =>
    request<DuelTournamentView & { replayed: boolean }>(`/duel-tournaments/${code}/join`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),
  cancelDuelTournament: (code: string, requestId: string) =>
    request<DuelTournamentView>(`/duel-tournaments/${code}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),
  duels: () =>
    request<
      readonly {
        id: string;
        code: string;
        status: 'OPEN' | 'ACCEPTED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
        createdAt: string;
        expiresAt: string;
        isCreator: boolean;
        winnerId: string | null;
        resolution: string | null;
        canAccept: boolean;
        canDecline: boolean;
        canCancel: boolean;
        canPlay: boolean;
        participants: readonly {
          id: string;
          isCurrentUser: boolean;
          score: number | null;
          scoreHidden: boolean;
          user: {
            id: string;
            displayName: string;
            avatarUrl?: string | null;
            social?: SocialIdentity;
          };
        }[];
      }[]
    >('/duels'),
  duel: (code: string) =>
    request<{
      id: string;
      code: string;
      creatorId: string;
      kind: 'DIRECT' | 'TOURNAMENT';
      entryFee: number;
      maxParticipants: number;
      prizePool: number;
      startedAt: string | null;
      winnerId: string | null;
      resolution: string | null;
      status: 'OPEN' | 'ACCEPTED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
      expiresAt: string;
      canAccept: boolean;
      canDecline: boolean;
      canCancel: boolean;
      canJoin: boolean;
      joinUnavailableReason: string | null;
      hasReplay: boolean;
      canPlay: boolean;
      participants: readonly {
        id: string;
        isCurrentUser: boolean;
        score: number | null;
        scoreHidden: boolean;
        user: {
          id: string;
          displayName: string;
          avatarUrl?: string | null;
          social?: SocialIdentity | null;
        };
      }[];
      rivalry: { played: number; wins: number; losses: number; draws: number; streak: number };
    }>(`/duels/${code}`),
  acceptDuel: (code: string) =>
    request<{ accepted: boolean }>(`/duels/${code}/accept`, { method: 'POST' }),
  declineDuel: (code: string) =>
    request<{ declined: boolean; replayed: boolean }>(`/duels/${code}/decline`, {
      method: 'POST',
    }),
  cancelDuel: (code: string) =>
    request<{ cancelled: boolean; replayed: boolean }>(`/duels/${code}/cancel`, {
      method: 'POST',
    }),
  rematchDuel: (code: string) =>
    request<{ code: string; startParameter: string }>(`/duels/${code}/rematch`, {
      method: 'POST',
    }),
  cosmetics: () => request<StoreData>('/cosmetics'),
  checkpointRun: (
    id: string,
    token: string,
    moves: readonly GameMove[],
    actions?: readonly DuelAction[],
  ) =>
    request<{ score: number; token: string }>(`/game/runs/${id}/checkpoint`, {
      method: 'POST',
      body: JSON.stringify({ token, moves, ...(actions ? { actions } : {}) }),
    }),
  searchPlayers: (query: string) =>
    request<
      readonly {
        id: string;
        displayName: string;
        avatarUrl: string | null;
        vipUntil?: string | null;
      }[]
    >(`/players/search?query=${encodeURIComponent(query)}`),
  gifts: () => request<readonly Record<string, unknown>[]>('/gifts'),
  giftCosmetic: (id: string, recipientUserId: string, idempotencyKey: string, message?: string) =>
    request<{ gifted: boolean; recipient: string; balance: number }>(`/cosmetics/${id}/gift`, {
      method: 'POST',
      body: JSON.stringify({ recipientUserId, idempotencyKey, ...(message ? { message } : {}) }),
    }),
  purchaseCosmetic: (id: string, ownership: 'PERMANENT' | 'RENTAL', idempotencyKey: string) =>
    request<{ purchased: boolean; balance: number }>(`/cosmetics/${id}/purchase`, {
      method: 'POST',
      body: JSON.stringify({ ownership, idempotencyKey }),
    }),
  purchaseClubCosmetic: (clubId: string, cosmeticId: string, idempotencyKey: string) =>
    request<{ purchased: boolean; balance: number }>(
      `/clubs/${clubId}/cosmetics/${cosmeticId}/purchase`,
      { method: 'POST', body: JSON.stringify({ idempotencyKey }) },
    ),
  activateCosmetic: (id: string) =>
    request<{ active: boolean }>(`/cosmetics/${id}/activate`, { method: 'POST' }),
  deactivateCosmetic: (id: string) =>
    request<{ active: boolean }>(`/cosmetics/${id}/deactivate`, { method: 'POST' }),
  updateNotifications: (values: {
    enabled: boolean;
    arenaReminders: boolean;
    duelNotifications: boolean;
  }) =>
    request<{ enabled: boolean; arenaReminders: boolean; duelNotifications: boolean }>(
      '/profile/notifications',
      { method: 'PATCH', body: JSON.stringify(values) },
    ),
  maxReferralCard: () =>
    request<{ botUrl: string }>('/profile/max-referral-card', { method: 'POST' }),
  buyYooKassaGemPack: (key: string, idempotencyKey: string, email: string) =>
    request<{ orderId: string; confirmationUrl: string }>(`/payments/yookassa/gem-packs/${key}`, {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey, email }),
    }),
  confirmVkGemPack: (key: string, orderId?: string, startedAt?: string) =>
    request<VkPurchaseConfirmation>(`/payments/vk/gem-packs/${key}/confirm`, {
      method: 'POST',
      body: JSON.stringify({
        ...(orderId ? { orderId } : {}),
        ...(startedAt ? { startedAt } : {}),
      }),
    }),
  paymentOrder: (id: string) =>
    request<{
      id: string;
      productKey: string | null;
      gemAmount: number;
      provider: string;
      status: string;
      createdAt: string;
      paidAt: string | null;
      balance: number;
      confirmationUrl: string | null;
    }>(`/payments/orders/${id}`),
  recoverPaymentOrder: (id: string) =>
    request<{ status: string }>(`/payments/orders/${id}/recover`, { method: 'POST' }),
  activateClubCosmetic: (clubId: string, cosmeticId: string) =>
    request<{ active: boolean }>(`/clubs/${clubId}/cosmetics/${cosmeticId}/activate`, {
      method: 'POST',
    }),
  deactivateClubCosmetic: (clubId: string, cosmeticId: string) =>
    request<{ active: boolean }>(`/clubs/${clubId}/cosmetics/${cosmeticId}/deactivate`, {
      method: 'POST',
    }),
  analyticsEvents: (events: readonly AnalyticsEvent[]) =>
    request<{ accepted: number; rejected?: number; disabled?: boolean }>('/analytics/events', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }),
  updateTutorial: (complete: boolean) =>
    request<{ tutorialComplete: boolean }>('/tutorial', {
      method: 'PATCH',
      body: JSON.stringify({ complete }),
    }),
};

export type ActivityTask = {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly description: string;
  readonly icon: string;
  readonly progress: number;
  readonly target: number;
  readonly reward: number;
  readonly rewardResource: 'PATH_COINS' | 'ENERGY';
  readonly cardRarity?: 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY' | null;
  readonly group?: 'DAILY' | 'BONUS' | 'WEEKLY' | 'INTRO' | 'SEASON';
  readonly metric?: string;
  readonly route?: '/path' | '/game/daily' | '/duels' | '/cards' | '/';
  readonly canReplace?: boolean;
  readonly completed: boolean;
  readonly claimed: boolean;
};

export type AnalyticsEventName =
  | 'diamond_value_viewed'
  | 'diamond_value_selected'
  | 'diamond_value_demo'
  | 'tutorial_viewed'
  | 'tutorial_started'
  | 'tutorial_first_piece_selected'
  | 'tutorial_first_piece_placed'
  | 'tutorial_first_line_cleared'
  | 'tutorial_phase_completed'
  | 'tutorial_hint_shown'
  | 'tutorial_invalid_action'
  | 'tutorial_skipped'
  | 'tutorial_completed'
  | 'tutorial_restarted'
  | 'tutorial_arena_cta_clicked'
  | 'home_view'
  | 'home_opened'
  | 'season_hub_viewed'
  | 'season_cta_clicked'
  | 'season_reward_claimed'
  | 'duel_picker_opened'
  | 'duel_candidate_selected'
  | 'duel_invite_shared'
  | 'home_arena_cta_view'
  | 'home_arena_cta_click'
  | 'home_active_goal_view'
  | 'home_goal_open'
  | 'home_reward_claim'
  | 'home_duel_click'
  | 'home_club_click'
  | 'home_cosmetic_preview'
  | 'home_cosmetic_purchase_click'
  | 'energy_panel_opened'
  | 'energy_referral_shared'
  | 'energy_bot_invite_opened'
  | 'energy_rewarded_ad_started'
  | 'energy_rewarded_ad_completed'
  | 'energy_rewarded_ad_failed'
  | 'result_replay_clicked'
  | 'result_energy_empty_viewed'
  | 'result_energy_invite_clicked'
  | 'result_energy_invite_completed'
  | 'game_start_energy_invite_clicked'
  | 'leagues_opened'
  | 'league_section_changed'
  | 'league_tab_changed'
  | 'league_progress_viewed'
  | 'league_group_viewed'
  | 'league_opponent_opened'
  | 'league_promoted'
  | 'league_reward_claimed'
  | 'daily_leaderboard_viewed'
  | 'global_leaders_opened'
  | 'weekly_tournament_viewed'
  | 'friends_leaderboard_viewed'
  | 'vk_invite_clicked'
  | 'duel_create_started'
  | 'duel_created'
  | 'duel_invite_sent'
  | 'duel_invite_failed'
  | 'clubs_leaderboard_viewed'
  | 'club_hub_viewed'
  | 'club_tab_changed'
  | 'club_chat_sent'
  | 'club_project_contributed'
  | 'club_vote_submitted'
  | 'league_cosmetic_opened'
  | 'store_opened'
  | 'commerce_products_viewed'
  | 'commerce_product_selected'
  | 'store_category_selected'
  | 'seasonal_collection_opened'
  | 'product_viewed'
  | 'product_preview_started'
  | 'product_preview_variant_changed'
  | 'product_purchase_clicked'
  | 'insufficient_gems_shown'
  | 'gem_store_opened'
  | 'gem_pack_selected'
  | 'platform_purchase_started'
  | 'gem_checkout_email_invalid'
  | 'gem_checkout_submitted'
  | 'gem_checkout_closed'
  | 'gem_payment_resumed'
  | 'gem_payment_status_checked'
  | 'platform_purchase_cancelled'
  | 'platform_purchase_pending'
  | 'platform_purchase_completed'
  | 'platform_purchase_failed'
  | 'cosmetic_purchase_completed'
  | 'cosmetic_equipped'
  | 'cosmetic_unequipped'
  | 'collection_opened'
  | 'gift_started'
  | 'gift_recipient_selected'
  | 'gift_completed'
  | 'earn_gems_clicked'
  | 'profile_opened'
  | 'public_profile_opened'
  | 'profile_customize_opened'
  | 'profile_cosmetic_equipped'
  | 'profile_showcase_opened'
  | 'profile_showcase_updated'
  | 'profile_achievement_opened'
  | 'profile_achievement_claimed'
  | 'profile_season_history_opened'
  | 'profile_club_opened'
  | 'profile_duel_clicked'
  | 'profile_share_clicked'
  | 'profile_invite_clicked'
  | 'profile_settings_opened'
  | 'profile_notification_changed'
  | 'profile_privacy_changed'
  | 'path_opened'
  | 'path_panel_opened'
  | 'path_level_started'
  | 'path_level_completed'
  | 'path_level_failed'
  | 'path_artifact_selected'
  | 'path_artifact_used'
  | 'path_artifact_purchase_started'
  | 'path_artifact_purchase_completed'
  | 'path_artifact_purchase_failed'
  | 'path_card_opened'
  | 'chat_opened'
  | 'chat_vip_gate'
  | 'vip_trial_offer_opened'
  | 'chat_commerce_opened'
  | 'chat_message_sent'
  | 'path_card_revealed'
  | 'path_card_claimed'
  | PathGrowthAnalyticsEventName;

export type AnalyticsEvent = {
  readonly name: AnalyticsEventName;
  readonly platform: 'VK' | 'MAX' | 'WEB';
  readonly sessionId: string;
  readonly clientVersion: string;
  readonly properties: Readonly<Record<string, string | number | boolean | null>>;
};

export type ChatPerson = { id: string; displayName: string; avatarUrl: string | null };
export type FriendConnection = {
  id: string;
  status: string;
  incoming: boolean;
  user: ChatPerson;
  lastMessage: { body: string; createdAt: string } | null;
  unread: boolean;
};
